/**
 * Formats a timestamp into a readable time format (e.g., "2:30 PM")
 * @param {string} timestamp - ISO timestamp string
 * @return {string} Formatted time string
 */
// Format timestamp for messages (e.g., "3:45 PM")
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;

  return `${formattedHours}:${formattedMinutes} ${ampm}`;
};

/**
 * Formats a timestamp into a readable date format
 * @param {string} timestamp - ISO timestamp string
 * @return {string} Formatted date string
 */
// Format date for message groups (e.g., "Aug 24, 2024")
export const formatDate = (timestamp) => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * Gets a snippet of the last message in a conversation
 * @param {Array} messages - Array of message objects
 * @return {string} Snippet of the last message or empty string
 */
// Get a snippet of the last message for chat previews
export const getLastMessageSnippet = (messages) => {
  if (!messages || messages.length === 0) return "";

  const lastMessage = messages[messages.length - 1].text;
  if (lastMessage.length > 30) {
    return lastMessage.substring(0, 30) + "...";
  }
  return lastMessage;
};

/**
 * Gets the formatted timestamp of the last message
 * @param {Array} messages - Array of message objects
 * @return {string} Formatted timestamp or empty string
 */
// Get the timestamp of the last message for chat previews
export const getLastMessageTimestamp = (messages) => {
  if (!messages || messages.length === 0) return "";
  return formatTimestamp(messages[messages.length - 1].timestamp);
};

/**
 * Store conversation IDs in localStorage for persistence
 * @param {string} userId - User ID
 * @param {string} conversationId - Conversation ID
 */
export const storeConversation = (userId, conversationId) => {
  try {
    const conversations =
      JSON.parse(localStorage.getItem("rently_conversations")) || {};
    conversations[userId] = conversationId;
    localStorage.setItem("rently_conversations", JSON.stringify(conversations));
    console.log(`Stored conversation ID ${conversationId} for user ${userId}`);
  } catch (error) {
    console.error("Error storing conversation:", error);
  }
};

/**
 * Retrieve stored conversation IDs
 * @return {Object} Object containing user IDs and conversation IDs
 */
export const getStoredConversations = () => {
  try {
    return JSON.parse(localStorage.getItem("rently_conversations")) || {};
  } catch (error) {
    console.error("Error retrieving conversations:", error);
    return {};
  }
};

/**
 * Store all conversation data from authentication response in localStorage
 * @param {Object} authResponse - Authentication response object
 */
export const storeConversationsFromAuth = (authResponse) => {
  try {
    // Verify this is an authentication response with conversations
    if (
      authResponse?.msg_type !== "user.auth.ack" ||
      !authResponse?.msg_res?.data?.conversations
    ) {
      return false;
    }

    const conversationsData = authResponse.msg_res.data.conversations;

    // Store full conversation data
    localStorage.setItem(
      "rently_conversations_full",
      JSON.stringify(conversationsData)
    );

    // Store userId for convenience
    if (authResponse.msg_res.data.userId) {
      localStorage.setItem("rently_user_id", authResponse.msg_res.data.userId);
    }

    // Also update the conversation mapping used by the existing code
    const conversationMap = {};
    conversationsData.forEach((conv) => {
      if (conv.other_user_id && conv.conversation_id) {
        conversationMap[conv.other_user_id] = conv.conversation_id;
      }
    });

    localStorage.setItem(
      "rently_conversations",
      JSON.stringify(conversationMap)
    );
    console.log("Stored all conversations from authentication response");

    return true;
  } catch (error) {
    console.error("Error storing conversations from auth response:", error);
    return false;
  }
};

/**
 * Get full conversation data stored from authentication
 * @returns {Array} Array of conversation objects or empty array
 */
export const getStoredConversationsFull = () => {
  try {
    return JSON.parse(localStorage.getItem("rently_conversations_full")) || [];
  } catch (error) {
    console.error("Error retrieving full conversations:", error);
    return [];
  }
};
